import { useEffect } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Bath,
    Droplets,
    Sparkles,
    ShieldCheck,
    Award,
    Clock,
    DollarSign,
    Wrench,
    Ruler,
    Home as HomeIcon,
    Star,
    Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

// Real 5 Star Construction bathroom remodel project photos.
const IMG_CULTURED = "/images/bathroom/projects/01-gray-marble-shower.webp";
const IMG_FIBERGLASS = "/images/bathroom/fiberglass-shower.jpg";
const IMG_FULL = "/images/bathroom/projects/04-full-remodel-wood-tub.jpg";
const IMG_INPROGRESS = "/images/bathroom/projects/05-gray-double-vanity.jpg";

// Recent-projects gallery — all 5 real photos. Hero uses PROJECT_GALLERY[0].
const PROJECT_GALLERY = [
    { src: "/images/bathroom/projects/02-luxury-marble-bath.png", alt: "Luxury full bathroom remodel with white cultured marble walk-in shower, matte-black rain shower head, floating dark double vanity, quartz countertops, and framed mirrors — Southern Oregon remodel by 5 Star Construction", cap: "Cultured marble master bath · double vanity" },
    { src: "/images/bathroom/projects/01-gray-marble-shower.webp", alt: "Gray-veined cultured marble sliding-door shower next to a freestanding soaker tub and white shaker double vanity — Medford bathroom remodel", cap: "Gray cultured marble · sliding door" },
    { src: "/images/bathroom/projects/03-dark-marble-shower.png", alt: "Dramatic dark blue and gray veined cultured marble walk-in shower with corner glass enclosure, chrome fixtures, and matching marble base — statement master bath", cap: "Statement marble · corner glass" },
    { src: "/images/bathroom/projects/04-full-remodel-wood-tub.jpg", alt: "Full bathroom remodel with freestanding soaker tub against a fluted wood accent wall, sputnik pendant lights, hexagon mosaic tile, marble floor, and double vanity — luxury remodel", cap: "Full remodel · freestanding tub" },
    { src: "/images/bathroom/projects/05-gray-double-vanity.jpg", alt: "Custom gray shaker double vanity with white quartz counter, chrome faucets, and gray marble tile floor — Southern Oregon bathroom remodel", cap: "Custom double vanity · quartz top" },
];
const IMG_HERO = PROJECT_GALLERY[0].src;

const REMODEL_TYPES = [
    {
        i: Sparkles,
        badge: "Custom · seamless · low-maintenance",
        title: "Cultured Marble Showers & Surrounds",
        blurb: "Custom-molded, seamless cultured marble shower walls and surrounds. No grout lines to scrub. Available in dozens of colors and veining patterns to match any bathroom style.",
        bullets: [
            "Seamless one-piece walls — no leaky grout lines",
            "Custom-fabricated to your exact shower dimensions",
            "Dozens of solid, veined, and speckled patterns",
            "Cultured marble shower pans, tub decks, and vanity tops",
            "Low-maintenance — a soft cloth is all you need",
            "Warm, luxurious feel underfoot",
        ],
        img: IMG_CULTURED,
    },
    {
        i: Droplets,
        badge: "Durable · fast · budget-friendly",
        title: "Fiberglass Showers & Tub Surrounds",
        blurb: "One-piece and multi-piece fiberglass shower stalls, tub/shower combos, and surrounds. The fastest, most affordable way to replace a tired old shower — installed clean in a day or two.",
        bullets: [
            "One-piece and 3–5 piece systems available",
            "Walk-in showers, tub/shower combos, or corner units",
            "Perfect for manufactured homes (our specialty)",
            "ADA-accessible low-threshold options",
            "Textured or smooth wall finishes",
            "Typically 1–2 day install turnaround",
        ],
        img: IMG_FIBERGLASS,
    },
    {
        i: HomeIcon,
        badge: "Complete transformation",
        title: "Full Bathroom Remodels",
        blurb: "Wall-to-wall bathroom transformations — demolition, plumbing rough-in, electrical, tile, drywall, cabinetry, countertops, vanities, and fixtures. One contractor. One schedule. One finish.",
        bullets: [
            "Full demo + haul-away",
            "Plumbing rough-in & fixture upgrades",
            "GFCI outlets, vanity lighting, exhaust fan upgrades",
            "Tile, cultured marble, or fiberglass wall systems",
            "Vanities, countertops, mirrors, and hardware",
            "Flooring: LVP, tile, or sheet vinyl",
        ],
        img: IMG_FULL,
    },
];

const WHY_US = [
    { i: Award, t: "Southern Oregon's Premier", d: "Bathroom remodels are our specialty — cultured marble, fiberglass, or full transformations." },
    { i: ShieldCheck, t: "Licensed & Bonded", d: "Oregon CCB #232477. Fully insured. 25 years on the tools." },
    { i: Clock, t: "On Schedule", d: "We quote the day, schedule the day, and finish the day. No ghosting, no drift." },
    { i: DollarSign, t: "Fair, Fixed Pricing", d: "Real numbers up front. No surprise change orders unless the wall's hiding something." },
];

const PROCESS = [
    { t: "Free in-home consult", d: "We measure, listen, and walk you through cultured marble vs. fiberglass vs. full-tile options — with real prices." },
    { t: "Design & material selection", d: "Pick your marble color, tile pattern, vanity, faucet, and fixtures. We handle the ordering." },
    { t: "Demo & rough-in", d: "Old shower out, plumbing repaired or upgraded, framing corrected, waterproofing done right." },
    { t: "Install & finish", d: "Walls, floor, vanity, fixtures, trim, paint. Sealed, tested, and cleaned up before we leave." },
];

const FAQ = [
    { q: "How long does a bathroom remodel take in Medford?", a: "Fiberglass shower swaps run 1–2 days. Cultured marble surrounds run 2–4 days including template + install. Full bathroom remodels typically run 2–4 weeks depending on scope, tile work, and materials lead time." },
    { q: "What's the difference between cultured marble, fiberglass, and tile?", a: "Cultured marble is a seamless custom-molded shower material — no grout, luxurious feel, mid-price point. Fiberglass is the fastest and most affordable — great for manufactured homes and rentals. Tile is fully custom but the most labor-intensive and requires ongoing grout maintenance." },
    { q: "Do you handle plumbing and electrical during a remodel?", a: "Yes. We coordinate all trades — plumbing rough-in, GFCI outlets, exhaust fan upgrades, vanity lighting. You get one contractor, one schedule, one point of contact." },
    { q: "Do you do bathroom remodels on manufactured homes?", a: "Yes — this is one of our specialties. Manufactured-home bathrooms have quirks (thinner walls, plastic drain lines, tub sizing), and we know them cold. Fiberglass and cultured marble both work great in these homes." },
    { q: "How much does a bathroom remodel cost in Southern Oregon?", a: "Fiberglass shower swaps typically start around $3,500–$6,500 installed. Cultured marble surrounds range $5,000–$9,000. Full bathroom remodels vary widely by scope — expect $12,000 on the low end for a small guest bath up to $40,000+ for a full master bath. Every quote is free and specific to your bathroom." },
];

// Popular cultured marble color options — CSS gradients simulate the marble look.
const MARBLE_COLORS = [
    { name: "Snow White", vibe: "Bright · classic", bg: "linear-gradient(135deg, #FAFAF6 0%, #ECE9E1 50%, #F2EFE8 100%)", vein: "radial-gradient(ellipse at 30% 20%, rgba(180,180,175,0.18) 0%, transparent 45%), radial-gradient(ellipse at 70% 75%, rgba(160,160,155,0.15) 0%, transparent 40%)", label: "#111812" },
    { name: "Bianco Carrara", vibe: "Veined · timeless", bg: "linear-gradient(135deg, #F2EFE8 0%, #E4E0D5 100%)", vein: "linear-gradient(115deg, transparent 45%, rgba(120,125,130,0.35) 47%, transparent 49%), linear-gradient(80deg, transparent 60%, rgba(110,115,120,0.28) 61%, transparent 63%), radial-gradient(ellipse at 20% 80%, rgba(140,145,150,0.22) 0%, transparent 40%)", label: "#111812" },
    { name: "Beige Travertine", vibe: "Warm · organic", bg: "linear-gradient(135deg, #E4D3B4 0%, #C9B48D 50%, #D4C39F 100%)", vein: "radial-gradient(ellipse at 25% 30%, rgba(140,110,70,0.2) 0%, transparent 40%), radial-gradient(ellipse at 75% 70%, rgba(120,95,60,0.18) 0%, transparent 45%)", label: "#111812" },
    { name: "Silver Frost", vibe: "Cool · modern", bg: "linear-gradient(135deg, #D4D5D3 0%, #B4B5B2 50%, #C8C9C6 100%)", vein: "linear-gradient(105deg, transparent 40%, rgba(90,92,95,0.3) 42%, transparent 44%), radial-gradient(ellipse at 65% 25%, rgba(160,165,170,0.35) 0%, transparent 50%)", label: "#111812" },
    { name: "Storm Gray", vibe: "Bold · sophisticated", bg: "linear-gradient(135deg, #7C7E80 0%, #545658 50%, #6A6C6E 100%)", vein: "linear-gradient(120deg, transparent 45%, rgba(190,192,195,0.28) 47%, transparent 50%), radial-gradient(ellipse at 30% 70%, rgba(200,205,210,0.2) 0%, transparent 45%)", label: "#F6F5F2" },
    { name: "Midnight Blue", vibe: "Dramatic · statement", bg: "linear-gradient(135deg, #2E3548 0%, #12172A 50%, #1E243A 100%)", vein: "linear-gradient(105deg, transparent 40%, rgba(220,225,235,0.35) 42%, transparent 45%), linear-gradient(75deg, transparent 60%, rgba(180,190,210,0.25) 62%, transparent 65%), radial-gradient(ellipse at 65% 30%, rgba(120,140,180,0.3) 0%, transparent 45%)", label: "#F6F5F2" },
];

export default function BathroomRemodel() {
    useSeo({
        title: "Southern Oregon's Premier Bathroom Remodel Company · Medford, OR | 5 Star Construction",
        description: "Southern Oregon's premier bathroom remodel contractor — cultured marble showers, fiberglass surrounds, and full bathroom remodels in Medford, Ashland, Grants Pass, and the Rogue Valley. Licensed Oregon GC (CCB #232477). Free in-home quotes — 541-292-9620.",
        path: "/bathroom-remodel",
        jsonLdKey: "bathroom-remodel",
        jsonLd: [
            serviceSchema({
                name: "Bathroom Remodeling — Cultured Marble, Fiberglass & Full Remodels",
                description: "Southern Oregon's premier bathroom remodeling company. Cultured marble showers and surrounds, fiberglass shower installations, and full bathroom remodels in Medford, Ashland, and the Rogue Valley. Manufactured-home bathroom specialist.",
                path: "/bathroom-remodel",
                serviceType: "Bathroom Remodeling Contractor",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Bathroom Remodel", path: "/bathroom-remodel" },
            ]),
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((it) => ({
                    "@type": "Question",
                    name: it.q,
                    acceptedAnswer: { "@type": "Answer", text: it.a },
                })),
            },
            {
                "@context": "https://schema.org",
                "@type": "ImageGallery",
                name: "Recent Bathroom Remodel Projects — 5 Star Construction, Medford, Oregon",
                description: "Real bathroom remodel projects completed by 5 Star Construction Services LLC across Medford, Ashland, and the Rogue Valley — cultured marble showers, full remodels, and custom vanities.",
                image: PROJECT_GALLERY.map((p) => ({
                    "@type": "ImageObject",
                    contentUrl: `https://5starhelpme.com${p.src}`,
                    url: `https://5starhelpme.com${p.src}`,
                    name: p.cap,
                    caption: p.alt,
                    creditText: "5 Star Construction Services LLC",
                    creator: {
                        "@type": "Organization",
                        name: "5 Star Construction Services LLC",
                    },
                    license: "https://5starhelpme.com/terms",
                })),
            },
        ],
    });
    useEffect(() => {}, []);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Bath className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Bathroom Remodel · Medford, Oregon</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Southern Oregon&apos;s
                            <br />
                            <span className="text-[#FFD400]">premier bathroom</span>
                            <br />
                            remodel company.
                        </h1>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-2xl">
                            Cultured marble showers, fiberglass surrounds, and full bathroom remodels — done right the first time. One licensed contractor, one crew, one finish. Serving Medford, Ashland, Central Point, Grants Pass, and the entire Rogue Valley.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="bath-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="bath-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    FREE IN-HOME QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-4 relative">
                        <div className="relative aspect-square w-full max-w-sm mx-auto lg:max-w-none overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block">
                            <img src={IMG_HERO} alt="Full luxury bathroom remodel with white cultured marble walk-in shower, matte-black rain shower head, dark floating double vanity, quartz counter, and framed mirrors — Southern Oregon" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute -top-4 -right-4 hidden md:flex w-32 h-32 bg-[#E11D2A] text-white items-center justify-center flex-col border-2 border-[#111111] shadow-block-sm rotate-3">
                                <div className="font-['Anton'] text-2xl tracking-tight uppercase leading-none">Free</div>
                                <div className="font-['Anton'] text-2xl tracking-tight uppercase leading-tight">Quotes</div>
                                <div className="font-['Manrope'] text-[9px] uppercase tracking-[0.18em] font-bold mt-1">Same-day call</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premier callout strip */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-6 md:py-8 flex flex-col sm:flex-row items-center gap-4 justify-center text-center">
                    <div className="flex items-center gap-2">
                        {[0,1,2,3,4].map((i) => (<Star key={i} className="w-5 h-5 fill-[#111111] text-[#111111]" strokeWidth={0} />))}
                    </div>
                    <div className="font-['Anton'] tracking-tight uppercase text-[#111111] text-xl sm:text-2xl md:text-3xl">
                        The Rogue Valley&apos;s Trusted Bathroom Remodel Contractor · <span className="text-[#E11D2A]">25 Years Building</span>
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why homeowners pick us</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                One crew.
                                <br />
                                One schedule.
                                <br />
                                One finish.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Bathroom remodels get expensive fast when you&apos;re juggling separate plumbers, tile guys, and finish carpenters. We handle every trade under one contractor, one schedule, and one line of accountability. You get the phone number of the guy actually on the job — not a call center.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {WHY_US.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6">
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Three remodel types (alternating layout) */}
            {REMODEL_TYPES.map(({ i: I, badge, title, blurb, bullets, img }, idx) => {
                const bg = idx % 2 === 0 ? "bg-[#F6F5F2]" : "bg-white";
                const reverse = idx % 2 === 1;
                return (
                    <section key={title} className={`${bg} border-b border-[#E2DFD8]`}>
                        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                            <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                                <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-lg lg:max-w-none mx-auto">
                                    <img src={img} alt={`${title} — example installation`} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                            </div>
                            <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                                <div className="inline-flex items-center gap-2 bg-[#111111] text-[#FFD400] px-3 py-1.5">
                                    <I className="w-4 h-4" strokeWidth={2.5} />
                                    <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase">0{idx + 1} · {badge}</span>
                                </div>
                                <h2 className="mt-4 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                    {title}
                                </h2>
                                <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                    {blurb}
                                </p>
                                <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                                    {bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 font-['Manrope'] text-sm text-[#111812]">
                                            <Check className="w-4 h-4 text-[#E11D2A] shrink-0 mt-1" strokeWidth={3} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                );
            })}

            {/* Cultured marble color options */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]" data-testid="bath-marble-colors">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Pick your look</div>
                            <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                Cultured marble color options.
                            </h2>
                        </div>
                        <div className="lg:col-span-5 lg:pb-2">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                A handful of the most-requested patterns we install. Dozens more available — bring inspiration photos, we&apos;ll match them.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                        {MARBLE_COLORS.map((c, i) => (
                            <div key={c.name} className="group" data-testid={`bath-marble-swatch-${i}`}>
                                <div
                                    className="aspect-square border-2 border-[#111111] shadow-block-sm relative overflow-hidden group-hover:shadow-block transition-shadow"
                                    style={{ background: c.bg }}
                                >
                                    <div className="absolute inset-0" style={{ backgroundImage: c.vein, mixBlendMode: "multiply" }} />
                                    <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 40%)" }} />
                                </div>
                                <div className="mt-3">
                                    <div className="font-display text-base md:text-lg font-extrabold text-[#111812] leading-tight">{c.name}</div>
                                    <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.18em] font-bold text-[#5F5F5F] mt-1">{c.vibe}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="font-['Manrope'] text-sm text-[#5F5F5F]">Prefer to see full-size samples in your bathroom?</div>
                        <a href={SITE.phoneHref} data-testid="bath-marble-cta">
                            <Button className="rounded-none h-11 px-5 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide font-['Manrope']">
                                <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                CALL FOR SAMPLES
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="aspect-[3/4] overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block bg-[#111111] max-w-sm lg:max-w-none mx-auto">
                            <img src={IMG_INPROGRESS} alt="Custom gray shaker double vanity with white quartz countertop, chrome fixtures, and gray marble tile floor — 5 Star Construction bathroom remodel finish work" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#FFD400]">How it works</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl">
                            Consult, design, build, finish.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/75 leading-relaxed max-w-2xl">
                            Every remodel follows the same clear four-step path. No surprises, no vague timelines, no ghosting halfway through.
                        </p>
                        <ol className="mt-8 space-y-5">
                            {PROCESS.map((s, idx) => (
                                <li key={s.t} className="flex gap-5 items-start" data-testid={`bath-step-${idx}`}>
                                    <div className="shrink-0 w-12 h-12 bg-[#FFD400] text-[#111111] flex items-center justify-center font-['Anton'] text-xl">
                                        0{idx + 1}
                                    </div>
                                    <div>
                                        <div className="font-display text-lg md:text-xl font-extrabold text-[#F6F5F2]">{s.t}</div>
                                        <p className="font-['Manrope'] text-base text-[#F6F5F2]/70 mt-1 leading-relaxed">{s.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* Materials-at-a-glance table */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-12 max-w-3xl mx-auto">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Which is right for you?</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Cultured marble vs. fiberglass vs. full tile.
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base text-[#5F5F5F]">
                            Every material has trade-offs. Here&apos;s the honest breakdown.
                        </p>
                    </div>
                    <div className="overflow-x-auto border border-[#E2DFD8]">
                        <table className="w-full text-left text-sm" data-testid="bath-compare-table">
                            <thead className="bg-[#111111] text-[#F6F5F2]">
                                <tr>
                                    <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Material</th>
                                    <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Install Time</th>
                                    <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Maintenance</th>
                                    <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Price Range</th>
                                    <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Best For</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                <tr>
                                    <td className="px-4 py-3 font-display font-extrabold text-[#111812]">Cultured Marble</td>
                                    <td className="px-4 py-3 font-['Manrope']">2–4 days</td>
                                    <td className="px-4 py-3 font-['Manrope'] text-[#5F5F5F]">Very low (no grout)</td>
                                    <td className="px-4 py-3 font-display text-[#E11D2A] font-extrabold">$$</td>
                                    <td className="px-4 py-3 font-['Manrope']">Custom look without tile upkeep</td>
                                </tr>
                                <tr className="bg-[#F6F5F2]">
                                    <td className="px-4 py-3 font-display font-extrabold text-[#111812]">Fiberglass</td>
                                    <td className="px-4 py-3 font-['Manrope']">1–2 days</td>
                                    <td className="px-4 py-3 font-['Manrope'] text-[#5F5F5F]">Very low</td>
                                    <td className="px-4 py-3 font-display text-[#E11D2A] font-extrabold">$</td>
                                    <td className="px-4 py-3 font-['Manrope']">Manufactured homes, rentals, quick swap</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-3 font-display font-extrabold text-[#111812]">Full Tile</td>
                                    <td className="px-4 py-3 font-['Manrope']">1–2 weeks</td>
                                    <td className="px-4 py-3 font-['Manrope'] text-[#5F5F5F]">Grout re-sealing</td>
                                    <td className="px-4 py-3 font-display text-[#E11D2A] font-extrabold">$$$</td>
                                    <td className="px-4 py-3 font-['Manrope']">Fully-custom master bath, resale value</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="mb-12 max-w-3xl">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Frequently asked</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Bathroom remodel questions we hear every week.
                        </h2>
                    </div>
                    <div className="space-y-4">
                        {FAQ.map((it, i) => (
                            <details key={it.q} className="group bg-white border border-[#E2DFD8] hover:border-[#111111] transition-colors" data-testid={`bath-faq-${i}`}>
                                <summary className="cursor-pointer list-none p-5 md:p-6 flex items-start justify-between gap-4 font-display text-base md:text-lg font-extrabold text-[#111812]">
                                    <span>{it.q}</span>
                                    <span className="text-[#E11D2A] font-['Anton'] text-2xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <div className="px-5 pb-5 md:px-6 md:pb-6 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">{it.a}</div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recent Bathroom Projects — real photos of jobs we've completed */}
            <section className="bg-white border-b border-[#E2DFD8]" data-testid="bath-gallery">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Recent projects</div>
                            <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                Real bathrooms. Real jobs. Rogue Valley homes.
                            </h2>
                        </div>
                        <div className="lg:col-span-5 lg:pb-2">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                A quick look at recent cultured marble showers, full remodels, and custom vanities we&apos;ve installed for homeowners across Medford, Ashland, and Southern Oregon.
                            </p>
                        </div>
                    </div>

                    {/* Feature tile + 4 supporting tiles grid */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
                        {/* Feature (wider, taller) */}
                        <a href="/#contact" data-testid="bath-gallery-tile-0" className="group md:col-span-4 md:row-span-2 relative block border-2 border-[#111111] overflow-hidden shadow-block bg-[#111111] aspect-[4/3] md:aspect-auto">
                            <img src={PROJECT_GALLERY[0].src} alt={PROJECT_GALLERY[0].alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#111111]/95 via-[#111111]/50 to-transparent p-5 md:p-7">
                                <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.22em] font-bold text-[#FFD400]">Featured project</div>
                                <div className="font-display text-lg md:text-2xl font-extrabold text-[#F6F5F2] mt-1">{PROJECT_GALLERY[0].cap}</div>
                            </div>
                        </a>
                        {/* Supporting tiles */}
                        {PROJECT_GALLERY.slice(1).map((p, i) => (
                            <a
                                href="/#contact"
                                key={p.src}
                                data-testid={`bath-gallery-tile-${i + 1}`}
                                className="group md:col-span-2 relative block border-2 border-[#111111] overflow-hidden shadow-block-sm bg-[#111111] aspect-[4/3]"
                            >
                                <img src={p.src} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#111111]/90 to-transparent p-3 md:p-4">
                                    <div className="font-['Manrope'] text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFD400] leading-tight">{p.cap}</div>
                                </div>
                            </a>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="font-['Manrope'] text-sm text-[#5F5F5F]">Want your bathroom in the next batch of photos?</div>
                        <a href="/#contact" data-testid="bath-gallery-cta">
                            <Button className="rounded-none h-11 px-5 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide font-['Manrope']">
                                REQUEST A FREE QUOTE
                                <ArrowUpRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Ready for your <span className="bg-[#E11D2A] text-[#F6F5F2] px-3 py-0.5">dream bathroom</span>?
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free in-home consultations. Honest pricing. Southern Oregon&apos;s premier bathroom remodel contractor — CCB #232477.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="bath-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="bath-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Attribution */}
            <section className="bg-[#111111] text-[#F6F5F2]/55">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    All photos on this page are of real bathroom remodel projects completed by 5 Star Construction Services LLC. Fiberglass example photo used for illustration.
                </div>
            </section>

            <Footer />
        </div>
    );
}
